import { LoadingStageModel } from '@kts-front/models';
import { BaseResponse, ILocalStore } from '@kts-front/types';
import { runInAction } from 'mobx';
import { z, ZodSchema } from 'zod';

import { NotificationsStore } from 'stores/global/RootStore/NotificationsStore';
import { CollectionStore } from 'stores/local/shared/CollectionStore';
import { logZodError } from 'utils/logZodError';

export type HandbookStoreOptions<Raw> = {
  entitySchema: ZodSchema;
  notificationStore: NotificationsStore;
  loadItems: () => Promise<BaseResponse<{ items: Raw[] }>>;
};

export class HandbookStore<E extends { id: number }, Raw> extends CollectionStore<E, E, number> implements ILocalStore {
  private readonly _notificationStore: NotificationsStore;
  private readonly _entitySchema: ZodSchema;
  readonly loadingStage = new LoadingStageModel();
  private readonly _loadItems: HandbookStoreOptions<Raw>['loadItems'];

  constructor({ loadItems, notificationStore, entitySchema }: HandbookStoreOptions<Raw>) {
    super({
      parseItem: (e) => e,
      getEntityKey: (e) => e.id,
    });

    this._notificationStore = notificationStore;
    this._entitySchema = entitySchema;

    this._loadItems = loadItems;
  }

  async load(): Promise<void> {
    if (this.loadingStage.isSuccess) {
      return;
    }

    this.loadingStage.loading();

    const response = await this._loadItems();

    runInAction(() => {
      if (response.isError) {
        this.loadingStage.error();
        this._notificationStore.showErrorByResponse(response);

        return;
      }

      const parsed = z
        .object({
          items: z.array(this._entitySchema),
        })
        .safeParse(response.data);

      if (parsed.error) {
        logZodError(parsed.error);
        this.loadingStage.error();
        this._notificationStore.showError({ message: parsed.error.message });

        return;
      }

      this.fromArray(parsed.data.items);
      this.loadingStage.success();
    });
  }
}
